import {
  isDeclinedByUser,
  type CalendarEvent,
} from '@electron/shared/meetings.types';
import { addDays } from 'date-fns/addDays';
import { addHours } from 'date-fns/addHours';
import { addMilliseconds } from 'date-fns/addMilliseconds';
import { areIntervalsOverlapping } from 'date-fns/areIntervalsOverlapping';
import { differenceInHours } from 'date-fns/differenceInHours';
import { eachHourOfInterval } from 'date-fns/eachHourOfInterval';
import { isSameDay } from 'date-fns/isSameDay';
import { roundToNearestHours } from 'date-fns/roundToNearestHours';
import { startOfDay } from 'date-fns/startOfDay';
import { startOfHour } from 'date-fns/startOfHour';

const MIN_VISIBLE_HOURS = 4;
const MAX_VISIBLE_HOURS = 12;
// Events further ahead than this are not synced, so a grid there would look free
const SYNCED_DAYS_AHEAD = 10;

export type TimeSpan = { start: Date; end: Date };

export type SnapshotBlockKind =
  | 'proposed'
  | 'previous'
  | 'duplicate'
  | 'conflict'
  | 'free'
  | 'tentative'
  | 'busy';

type TimedItem = TimeSpan & {
  id: string;
  title: string;
  kind: SnapshotBlockKind;
};

export type SnapshotBlock = TimedItem & {
  isPast: boolean;
  topPercent: number;
  heightPercent: number;
  lane: number;
  laneCount: number;
};

// One day column: the hours it shows, and the whole days its events are fetched for
export type SnapshotColumnPlan = {
  day: Date;
  window: TimeSpan;
  fetch: TimeSpan;
};

export type SnapshotPlan = {
  hourMarks: Date[];
  columns: SnapshotColumnPlan[];
};

export type SnapshotColumn = {
  day: Date;
  blocks: SnapshotBlock[];
  allDayTitles: string[];
  nowPercent: number | null;
};

export type SnapshotSlot = TimeSpan & { title: string };

const BLOCK_ORDER: Record<SnapshotBlockKind, number> = {
  proposed: 0,
  previous: 1,
  duplicate: 2,
  conflict: 2,
  free: 2,
  tentative: 2,
  busy: 2,
};

const isSpan = (span: TimeSpan): boolean => span.start < span.end;

const normalizeTitle = (title: string): string =>
  title.trim().replace(/\s+/g, ' ').toLowerCase();

const titleOf = (event: CalendarEvent): string =>
  event.summary.trim() || '(No title)';

const spanOf = (event: CalendarEvent): TimeSpan => ({
  start: new Date(event.start_datetime),
  end: new Date(event.end_datetime),
});

// The hours of day every slot covers, padded to a few visible hours. Each column shows
// the same hours of its own day, so the slots of two days share one hour axis
export const planCalendarSnapshot = ({
  proposed,
  previous,
  nowMs,
}: {
  proposed: TimeSpan;
  previous: TimeSpan | null;
  nowMs: number;
}): SnapshotPlan | null => {
  if (!isSpan(proposed) || proposed.end.getTime() <= nowMs) return null;
  const slots =
    previous && isSpan(previous) ? [proposed, previous] : [proposed];
  const days = slots
    .map((slot) => startOfDay(slot.start))
    .sort((a, b) => a.getTime() - b.getTime())
    .filter(
      (day, index, sorted) => index === 0 || !isSameDay(day, sorted[index - 1])
    );

  const coveredStartHour = Math.min(
    ...slots.map((slot) =>
      differenceInHours(startOfHour(slot.start), startOfDay(slot.start))
    )
  );
  const coveredEndHour = Math.max(
    ...slots.map((slot) =>
      differenceInHours(
        roundToNearestHours(slot.end, { roundingMethod: 'ceil' }),
        startOfDay(slot.start)
      )
    )
  );
  const coveredHours = coveredEndHour - coveredStartHour;
  const visibleHours = Math.min(
    Math.max(coveredHours + 2, MIN_VISIBLE_HOURS),
    MAX_VISIBLE_HOURS
  );
  const leadingHours = Math.max(
    1,
    Math.floor((visibleHours - coveredHours) / 2)
  );
  const windowStartHour = coveredStartHour - leadingHours;
  if (windowStartHour + visibleHours < coveredEndHour) return null;

  const columns = days.map((day) => {
    const windowStart = addHours(day, windowStartHour);
    const windowEnd = addHours(windowStart, visibleHours);
    return {
      day,
      window: { start: windowStart, end: windowEnd },
      fetch: {
        start: startOfDay(windowStart),
        end: addDays(startOfDay(addMilliseconds(windowEnd, -1)), 1),
      },
    };
  });
  const proposedColumn = columns.find((column) =>
    isSameDay(column.day, proposed.start)
  );
  const isSynced =
    proposedColumn !== undefined &&
    proposedColumn.window.end <= addDays(nowMs, SYNCED_DAYS_AHEAD);
  if (!isSynced) return null;

  return { hourMarks: eachHourOfInterval(columns[0].window), columns };
};

const assignLanes = (
  items: TimedItem[]
): Array<TimedItem & { lane: number; laneCount: number }> => {
  const sorted = [...items].sort(
    (a, b) =>
      a.start.getTime() - b.start.getTime() ||
      BLOCK_ORDER[a.kind] - BLOCK_ORDER[b.kind] ||
      b.end.getTime() - a.end.getTime()
  );
  const placed: Array<TimedItem & { lane: number; laneCount: number }> = [];
  let cluster: Array<TimedItem & { lane: number }> = [];
  let laneEnds: number[] = [];
  let clusterEnd = -Infinity;

  const closeCluster = () => {
    const laneCount = laneEnds.length;
    placed.push(...cluster.map((item) => ({ ...item, laneCount })));
    cluster = [];
    laneEnds = [];
  };

  for (const item of sorted) {
    const startMs = item.start.getTime();
    const endMs = item.end.getTime();
    if (startMs >= clusterEnd) {
      closeCluster();
      clusterEnd = -Infinity;
    }
    const freeLane = laneEnds.findIndex((laneEnd) => laneEnd <= startMs);
    const lane = freeLane === -1 ? laneEnds.length : freeLane;
    laneEnds[lane] = endMs;
    cluster.push({ ...item, lane });
    clusterEnd = Math.max(clusterEnd, endMs);
  }
  closeCluster();
  return placed;
};

export const buildSnapshotColumn = ({
  column,
  proposed,
  previous,
  events,
  excludedEventId,
  nowMs,
}: {
  column: Pick<SnapshotColumnPlan, 'day' | 'window'>;
  proposed: SnapshotSlot;
  previous: TimeSpan | null;
  events: readonly CalendarEvent[];
  excludedEventId: string | null;
  nowMs: number;
}): SnapshotColumn => {
  const { window, day } = column;
  const windowStartMs = window.start.getTime();
  const windowMs = window.end.getTime() - windowStartMs;
  const toPercent = (date: Date) => {
    const clampedMs = Math.min(
      Math.max(date.getTime() - windowStartMs, 0),
      windowMs
    );
    return (clampedMs / windowMs) * 100;
  };

  const proposedTitle = normalizeTitle(proposed.title);
  const isDuplicate = (event: CalendarEvent, span: TimeSpan) =>
    proposedTitle !== '' &&
    normalizeTitle(titleOf(event)) === proposedTitle &&
    span.start.getTime() === proposed.start.getTime() &&
    span.end.getTime() === proposed.end.getTime();
  const kindOf = (event: CalendarEvent, span: TimeSpan): SnapshotBlockKind => {
    if (isDuplicate(event, span)) return 'duplicate';
    if (event.shows_as_free) return 'free';
    if (areIntervalsOverlapping(span, proposed)) return 'conflict';
    return event.status === 'tentative' ? 'tentative' : 'busy';
  };

  const shownEvents = events
    .filter((event) => event.id !== excludedEventId && !isDeclinedByUser(event))
    .map((event) => ({ event, span: spanOf(event) }))
    .filter(({ span }) => isSpan(span));
  const timedItems: TimedItem[] = shownEvents
    .filter(
      ({ event, span }) =>
        !event.is_all_day && areIntervalsOverlapping(span, window)
    )
    .map(({ event, span }) => ({
      ...span,
      id: event.id,
      title: titleOf(event),
      kind: kindOf(event, span),
    }));

  const slotTitle = proposed.title.trim() || 'New event';
  const slotItems: TimedItem[] = [
    {
      start: proposed.start,
      end: proposed.end,
      title: slotTitle,
      id: 'proposed',
      kind: 'proposed' as const,
    },
    ...(previous && isSpan(previous)
      ? [
          {
            ...previous,
            title: slotTitle,
            id: 'previous',
            kind: 'previous' as const,
          },
        ]
      : []),
  ].filter((slot) => areIntervalsOverlapping(slot, window));

  const blocks = assignLanes([...slotItems, ...timedItems]).map((item) => {
    const topPercent = toPercent(item.start);
    return {
      ...item,
      isPast: item.end.getTime() <= nowMs,
      topPercent,
      heightPercent: toPercent(item.end) - topPercent,
    };
  });

  const daySpan = { start: day, end: addDays(day, 1) };
  const nowInWindow = nowMs >= windowStartMs && nowMs <= window.end.getTime();

  return {
    day,
    blocks,
    allDayTitles: shownEvents
      .filter(
        ({ event, span }) =>
          event.is_all_day && areIntervalsOverlapping(span, daySpan)
      )
      .map(({ event }) => titleOf(event)),
    nowPercent: nowInWindow ? toPercent(new Date(nowMs)) : null,
  };
};

const utcOffsetAt = (instant: Date, timeZone: string): string | null => {
  try {
    return (
      new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longOffset' })
        .formatToParts(instant)
        .find((part) => part.type === 'timeZoneName')?.value ?? null
    );
  } catch {
    // Intl throws on a zone it cannot resolve, such as a model's 'America/Sao Paulo'
    return null;
  }
};

// The hours show in the device's zone. When the event was scheduled in a zone with
// another offset, the grid names the device's zone so its hours are not misread
export const deviceZoneLabelIfDifferent = (
  instant: Date,
  eventTimeZone: string | null | undefined,
  deviceTimeZone: string
): string | null => {
  if (!eventTimeZone) return null;
  const deviceOffset = utcOffsetAt(instant, deviceTimeZone);
  if (
    deviceOffset !== null &&
    deviceOffset === utcOffsetAt(instant, eventTimeZone)
  )
    return null;
  return (
    new Intl.DateTimeFormat([], {
      timeZone: deviceTimeZone,
      timeZoneName: 'short',
    })
      .formatToParts(instant)
      .find((part) => part.type === 'timeZoneName')?.value ?? deviceTimeZone
  );
};
